import React, { useContext, useEffect, useState } from 'react'
import {
  Modal,
  space,
  Table,
  TableRow,
  Text,
  useModalModel,
  Flex,
  getLastPage,
  usePaginationModel,
  CanvasProvider,
  Pagination,
  getVisibleResultsMin,
  getVisibleResultsMax,
  InputGroup,
  useInitialFocus,
  useAssistiveHideSiblings,
  useDisableBodyScroll,
  useFocusTrap,
  useReturnFocus,
  SystemIcon,
  PrimaryButton
} from '@workday/canvas-kit-react'
import { InputGroupInnerEnd, InputGroupInput } from '@workday/canvas-kit-react/dist/es6/text-input/lib/InputGroup'
import { CircularProgress } from '@material-ui/core'
import { checkIcon } from '@workday/canvas-system-icons-web'
import { ScreenWidthCategoryContext, ScreenWidthCategory } from '../../utils/ScreenWidthCategoryContext'
import { fetchAgencySearchResultsWithToken } from '../models/WebAppRequests'
import { ComponentTheme, PrimaryButtonColors } from '../../components/CanvasThemes'
import { useDebounce } from '../../utils/useDebounce'
import { AgencySearchItemModel } from '../../models/AgencySearchItemModel'
import styled from '@emotion/styled'
import { MAX_AGENCIES } from '../models/FeedConfiguration'
import { UpdateToastContext } from '../../utils'

export interface RespondAgencySelectorProps {
  selectedAgencyIDs: string[]
  onSelectAgencyIDs: (agencyIDs: string[]) => void
  onCancel: () => void
}

const StyledModalOverlay = styled(Modal.Overlay)`
  >div {
    align-items: center!important;
  }
`

const RespondAgencySelector = ({ selectedAgencyIDs, onSelectAgencyIDs, onCancel }: RespondAgencySelectorProps) => {
  const [enteredAgencyIDs, setEnteredAgencyIDs] = useState<string[]>(selectedAgencyIDs)
  const [enteredSearchText, setEnteredSearchText] = useState('')
  const [searchResults, setSearchResults] = useState<AgencySearchItemModel[]>([])
  const [loading, setLoading] = useState(false)
  const toast = useContext(UpdateToastContext)

  const screenWidthCategory = useContext(ScreenWidthCategoryContext)

  const debounceValue = useDebounce(enteredSearchText, 1000)
  useEffect(() => {
    const debounce = debounceValue
    setLoading(true)
    fetchAgencySearchResultsWithToken(debounceValue, 'PRODUCTION', undefined).then(results => {
      if (debounce === debounceValue) {
        setSearchResults(results)
        setLoading(false)
      }
    })
  }, [debounceValue])

  const pageChangeHandler = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const results = searchResults

  const resultsPerPage = 5
  const totalResults = results.length
  const [currentPage, setCurrentPage] = useState<number>(1)
  const lastPage = getLastPage(resultsPerPage, totalResults)
  const pageModel = usePaginationModel({
    initialCurrentPage: currentPage,
    lastPage: lastPage,
    onPageChange: pageChangeHandler,
    rangeSize: screenWidthCategory <= ScreenWidthCategory.ExtraSmall ? 3 : 5
  })
  const visibleResults = results.slice(
    getVisibleResultsMin(currentPage, resultsPerPage) - 1,
    getVisibleResultsMax(currentPage, resultsPerPage, totalResults)
  )

  const resultsJSON = JSON.stringify(results)
  useEffect(() => {
    setCurrentPage(1)
    pageModel.events.goTo(1)
  }, [resultsJSON]) // eslint-disable-line react-hooks/exhaustive-deps

  const model = useModalModel({
    onHide: () => {
      onCancel()
    }
  })
  useInitialFocus(model)
  useReturnFocus(model)
  useFocusTrap(model)
  useAssistiveHideSiblings(model)
  useDisableBodyScroll(model)
  model.state.visibility = 'visible'

  return (
    <Modal model={model}>
      <StyledModalOverlay>
        <Modal.Card style={{ width: '400px' }}>
          <Modal.Heading>Select Agencies</Modal.Heading>
          <Modal.Body>
            <Flex flexDirection="column" gap={space.xs}>
              <InputGroup>
                <InputGroupInput
                  placeholder="Search agency, city or zip code"
                  value={enteredSearchText}
                  onChange={(event: any) => {
                    setEnteredSearchText(event.target.value)
                  }}
                  grow
                />
                <InputGroupInnerEnd>{loading && <CircularProgress size={12} />}</InputGroupInnerEnd>
              </InputGroup>

              {totalResults > 0 && (
                <Table>
                  <tbody>
                    {visibleResults.map((result: AgencySearchItemModel, index: number) => {
                      return (
                        <TableRow
                          key={result.agencyID}
                          id={result.agencyID}
                          onClick={(event: any) =>
                            setEnteredAgencyIDs(prev => {
                              if (prev.length < MAX_AGENCIES) {
                                if (prev.includes(result.agencyID)) {
                                  return prev.filter(agencyID => agencyID !== result.agencyID)
                                } else {
                                  return [...prev, result.agencyID]
                                }
                              } else {
                                toast.setMessages([],['You\'ve reached the maximum number of agencies allowed.'])
                                return prev
                              }
                            })
                          }
                        >
                          <td id={result.agencyID}>
                            <Flex flexDirection="row" alignItems="center">
                              <Flex flexDirection="column" gap={0} alignItems="start">
                                <Text typeLevel="subtext.large" fontWeight="medium">
                                  {result.display1}
                                </Text>
                                <Text typeLevel="subtext.medium">{result.truncatedDisplay2()}</Text>
                              </Flex>
                              <Flex.Item flex={1} />
                              {enteredAgencyIDs.includes(result.agencyID) && <SystemIcon icon={checkIcon} />}
                            </Flex>
                          </td>
                        </TableRow>
                      )
                    })}
                  </tbody>
                </Table>
              )}
              {totalResults > 0 && (
                <CanvasProvider theme={ComponentTheme}>
                  <Flex justifyContent="space-around">
                    <Pagination aria-label="Pagination" model={pageModel}>
                      <Pagination.Controls>
                        <Pagination.StepToPreviousButton aria-label="Previous" />
                        <Pagination.PageList>
                          {({ state }) =>
                            state.range.map(pageNumber => (
                              <Pagination.PageListItem key={pageNumber}>
                                <Pagination.PageButton aria-label={`Page ${pageNumber}`} pageNumber={pageNumber} />
                              </Pagination.PageListItem>
                            ))
                          }
                        </Pagination.PageList>
                        <Pagination.StepToNextButton aria-label="Next" />
                      </Pagination.Controls>

                      <Pagination.AdditionalDetails>
                        {({ state }) =>
                          `${getVisibleResultsMin(state.currentPage, resultsPerPage)}-${getVisibleResultsMax(
                            state.currentPage,
                            resultsPerPage,
                            totalResults
                          )} of ${totalResults} results`
                        }
                      </Pagination.AdditionalDetails>
                    </Pagination>
                  </Flex>
                </CanvasProvider>
              )}
              <Text typeLevel="subtext.medium" textAlign="center">
                PulsePoint is not available in all areas. The service is only offered where adopted by the local public
                safety agency.
              </Text>
              <Flex flexDirection="row" gap={space.xs}>
                <PrimaryButton colors={PrimaryButtonColors} onClick={event => onSelectAgencyIDs(enteredAgencyIDs)}>
                  Save
                </PrimaryButton>
                <Modal.CloseButton>Cancel</Modal.CloseButton>
              </Flex>
            </Flex>
          </Modal.Body>
        </Modal.Card>
      </StyledModalOverlay>
    </Modal>
  )
}

export default RespondAgencySelector
